import { useState, useEffect, useContext, useCallback } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import UserContext from '../../../../store/user-context';
import { ModalContext } from '../../../../store/modal-context';

import Hide from '../../../../assets/icons/hide.png';
import View from '../../../../assets/icons/view.png';

import useFetch from '../../../../hooks/use-fetch';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import HtmlInput from '../../../UI/HtmlInput';
import useAnalyticsEventTracker from '../../../../hooks/use-analytics';

const Login = ({ setActiveTab, originalReferer }) => {
  const { t: translate } = useTranslation();
  const { setUser } = useContext(UserContext);
  const { modal, handleModal } = useContext(ModalContext);
  const history = useHistory();
  const { apiUrl } = window['appConfig'];
  const profileRedirect = JSON.parse(localStorage.getItem('profileRedirect'));
  const gaEventTracker = useAnalyticsEventTracker('Map');
  const [loginErrors, setLoginErrors] = useState('');

  const {
    data: loginData,
    loading: isFetching,
    error: fetchErrors,
    initFunc: callFetch,
  } = useFetch();

  const loginFields = {
    email: '',
    password: '',
  };

  const [loginValues, setLoginValues] = useState(loginFields);
  const [loginFieldErrors, setLoginFieldErrors] = useState({});
  const [showPass, setShowPass] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passIcon = showPass === true ? Hide : View;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (Object.prototype.hasOwnProperty.call(loginFieldErrors, name)) {
      //reset errors
      setLoginFieldErrors({});
    }

    setLoginValues({
      ...loginValues,
      [name]: value,
    });
  };

  const passwordToggle = useCallback(() => {
    setShowPass((showPass) => !showPass);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();

    //remove all errors
    setLoginFieldErrors({});

    if (!loginValues.email) {
      setLoginFieldErrors((prevState) => ({
        ...prevState,
        email: translate('email-required'),
      }));

      return;
    }

    if (!loginValues.password) {
      setLoginFieldErrors((prevState) => ({
        ...prevState,
        password: translate('password-required'),
      }));

      return;
    }

    setIsSubmitting(true);
  };

  const attemptLogin = () => {
    const formBody = Object.keys(loginValues)
      .map(
        (key) =>
          encodeURIComponent(key) + '=' + encodeURIComponent(loginValues[key]),
      )
      .join('&');

    const referer =
      originalReferer || !document.referrer ? '' : document.referrer;
    const loginOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Original-Referer': `${referer}`,
      },
      body: formBody,
    };

    callFetch({
      url: `${apiUrl}/user/login`,
      options: loginOptions,
    });
  };

  const handleLoginData = (loginData) => {
    //first check if the error is an unconfirmed user issue.
    if (loginData.confirmed === false) {
      history.push(
        `/confirm-email?email=${encodeURIComponent(loginValues.email)}`,
      );
      return;
    }

    //Login failed, exit early so errors can be displayed to user
    if (loginData.error !== null) return;

    //Set user data
    const userData = {
      jwtToken: loginData.jwtToken,
      refreshToken: loginData.refreshToken,
      confirmed: loginData.confirmed,
      user: loginData.user,
    };

    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));

    if (modal) {
      //Hide Modal
      handleModal();

      window.location.reload();
    } else {
      //Redirect to town page
      profileRedirect === '1'
        ? history.push('/user/dashboard')
        : history.push('/');
      localStorage.setItem('profileRedirect', JSON.stringify('0'));
    }
  };

  useEffect(() => {
    if (Object.keys(loginFieldErrors).length === 0 && isSubmitting) {
      attemptLogin();
    }
  }, [loginFieldErrors]);

  useEffect(() => {
    if (fetchErrors) {
      setLoginErrors(fetchErrors);
    }

    if (loginData !== null) {
      if (loginData.passwordResetRequired === true) {
        history.push(
          `/forgottenpass?forced=true&email=${encodeURIComponent(
            loginValues.email,
          )}`,
        );
      } else if (loginData.known === false) {
        //TODO: Translate this
        let unknownContent = (
          <span>
            Unrecognised email. Why not{' '}
            <button
              className="button button--alt button--tiny"
              onClick={() => {
                setActiveTab('signup');
              }}>
              Create an Account?
            </button>
          </span>
        );
        setLoginErrors(unknownContent);
      } else {
        handleLoginData(loginData);
      }
    }
  }, [loginData, fetchErrors]);

  return (
    <form className="login-form" onSubmit={handleLogin}>
      <div
        className={`input-row ${
          loginFieldErrors.email ? 'input-row--error' : ''
        }`}>
        <HtmlInput
          type="email"
          id="email"
          name="email"
          legend={translate('email')}
          value={loginValues.email}
          changeCallback={handleChange}
          hasErrors={'email' in loginFieldErrors}
        />
        {loginFieldErrors.email && (
          <p className="input-row__error">{loginFieldErrors.email}</p>
        )}
      </div>

      <div
        className={`input-row input-row--password ${
          loginFieldErrors.password ? 'input-row--error' : ''
        }`}>
        <HtmlInput
          type={showPass === true ? 'text' : 'password'}
          id="password"
          name="password"
          legend={translate('password')}
          value={loginValues.password}
          changeCallback={handleChange}
          hasErrors={'password' in loginFieldErrors}
          detectCapsLock={true}
        />
        <span className="visibility" onClick={passwordToggle}>
          <img
            src={passIcon}
            alt={translate('toggle-password')}
            title={translate('toggle-password')}
          />
        </span>

        {loginFieldErrors.password && (
          <p className="input-row__error">{loginFieldErrors.password}</p>
        )}
      </div>
      {loginErrors && <p className="error-field">{loginErrors}</p>}
      <button
        className={`button landing-button button--success ${
          isFetching ? 'button--loading' : ''
        }`}
        disabled={isFetching ? true : false}
        onClick={() => {
          gaEventTracker('Login-Clicked', '');
        }}>
        <span>{translate('login')}</span>
        <FontAwesomeIcon icon={faChevronRight} size="1x" />
      </button>
      <Link to="/forgottenpass" className="button button--tiny">
        {translate('forgotten-password')}
      </Link>
    </form>
  );
};

export default Login;
